import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ArrowOutward } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import {
  Estate_FieldsFragment,
  EstateTask_GetAllFavoriteEstateTasksQuery,
  EstateTaskIdentifier,
  EstateTaskInstance,
  EstateTaskInstance_FieldsFragment,
  EstateTaskInstanceStatus,
  useEstateTask_GetAllFavoriteEstateTasksQuery,
  useEstateTaskInstance_GetEstateTaskInstanceByEstateIdAndEstateTaskIdLazyQuery,
  useFeatureSubscription_GetFeatureSubscriptionTypeQuery,
} from 'queries';

import Spinner from 'common/spinner';
import CreatePaymentModal from 'pages/common/estate/create-payment-modal';
import EstateTaskInstancePaymentForm from 'pages/estate-checklist/components/estate-task/estate-task-instance-payment-form';
import EstateTrusuiteSearchPaymentForm from 'pages/estate-checklist/components/estate-trusuite-search-payment-form';
import { canAssignTasks } from 'utils/feature-checker';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

import { PortalFavoriteTasksSelectEstateModal } from './portal-favorite-tasks-select-estate-modal/portal-favorite-tasks-select-estate-modal';
import { useStyles } from './portal-favorite-tasks.styles';

type PortalFavoriteTasksProps = {
  portalId: string;
};

type FavoriteEstateTask = EstateTask_GetAllFavoriteEstateTasksQuery['data'][number];

export const PortalFavoriteTasks = ({ portalId }: PortalFavoriteTasksProps) => {
  const classes = useStyles();
  const history = useHistory();
  const [selectedFavoriteTask, setSelectedFavoriteTask] = useState<FavoriteEstateTask | null>(null);
  const [selectedEstate, setSelectedEstate] = useState<Estate_FieldsFragment | null>(null);
  const [showTrusuiteSearchPaymentModal, setShowTrusuiteSearchPaymentModal] = useState(false);
  const [showDelegatePaymentModal, setShowDelegatePaymentModal] = useState(false);
  const [estateTaskInstanceCurrentlyEditing, setEstateTaskInstanceCurrentlyEditing] =
    useState<EstateTaskInstance_FieldsFragment>();
  const [showSelectEstateModal, setShowSelectEstateModal] = useState(false);

  const { data: featureSubscriptionTypeData, loading: featureSubscriptionTypeLoading } =
    useFeatureSubscription_GetFeatureSubscriptionTypeQuery({
      variables: { portalId },
      onError: (e) =>
        showAlert({
          message: 'Error getting feature subscription type: ' + e.message,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const { data: favoriteEstateTasksData, loading: isGetAllFavoriteEstateTasksLoading } =
    useEstateTask_GetAllFavoriteEstateTasksQuery({
      skip: !portalId,
      variables: { portalId },
      onError: (e) =>
        showAlert({
          message: 'Error fetching favorite tasks: ' + e.message,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const [getEstateTaskInstanceForEstateTask] =
    useEstateTaskInstance_GetEstateTaskInstanceByEstateIdAndEstateTaskIdLazyQuery();
  const favoriteEstateTasks = favoriteEstateTasksData?.data || [];

  const resetStates = () => {
    setSelectedFavoriteTask(null);
    setSelectedEstate(null);
    setShowTrusuiteSearchPaymentModal(false);
    setShowDelegatePaymentModal(false);
    setEstateTaskInstanceCurrentlyEditing(undefined);
    setShowSelectEstateModal(false);
  };

  const handleSelectFavoriteTask = (favoriteTask: FavoriteEstateTask) => {
    resetStates();
    setSelectedFavoriteTask(favoriteTask);
    setShowSelectEstateModal(true);
  };

  const handleAssignTask = (estateTaskInstance: EstateTaskInstance_FieldsFragment) => {
    if (estateTaskInstance.status === EstateTaskInstanceStatus.Assigned) {
      showToast({
        message:
          'This task is already assigned for this estate. Please check the Assigned Tasks page.',
        severity: NotificationSeverity.WARNING,
      });
      return;
    }
    if (estateTaskInstance.identifier === EstateTaskIdentifier.TrusuiteSearch) {
      setShowTrusuiteSearchPaymentModal(true);
      return;
    }
    setEstateTaskInstanceCurrentlyEditing(estateTaskInstance);
    setShowDelegatePaymentModal(true);
  };

  const handleSelectEstate = async (estate: Estate_FieldsFragment) => {
    if (!canAssignTasks(featureSubscriptionTypeData!.data)) {
      showToast({
        message: 'Please upgrade your account to push this task to Trustate.',
        severity: NotificationSeverity.WARNING,
      });
      return;
    }
    setSelectedEstate(estate);

    await getEstateTaskInstanceForEstateTask({
      onCompleted: (data) => handleAssignTask(data.data),
      variables: {
        input: {
          estateId: estate.id,
          estateTaskId: selectedFavoriteTask!.id,
          portalId,
        },
      },
    });
  };

  const onAssignTask = () => {
    resetStates();
    history.push(`/home/${portalId}/assigned-tasks`);
  };

  if (featureSubscriptionTypeLoading || isGetAllFavoriteEstateTasksLoading) {
    return <Spinner />;
  }

  return (
    <Box className={classes.container}>
      <Typography className={classes.sectionTitle}>Jump in!</Typography>
      <Box className={classes.favoriteTasksContainer}>
        {favoriteEstateTasks.map((task) => (
          <Box
            key={task.id}
            className={classes.favoriteTaskContainer}
            onClick={() => handleSelectFavoriteTask(task)}
          >
            <Box display="flex" flexDirection="row" justifyContent="space-between" width="100%">
              {task.iconUrl && (
                <Box display="flex">
                  <img src={task.iconUrl} alt={task.shortName || 'Favorite Task Icon'} />
                </Box>
              )}
              <ArrowOutward />
            </Box>
            <Box mt={1}>
              <Typography variant="body1" fontWeight="bold" className={classes.favoriteTaskTitle}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: task.shortName || task.label,
                  }}
                ></div>
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
      {selectedFavoriteTask && showSelectEstateModal && (
        <PortalFavoriteTasksSelectEstateModal
          setShowModal={resetStates}
          showModal={!!selectedFavoriteTask}
          estateType={selectedFavoriteTask.estateType}
          onSelectEstate={(estate) => {
            setShowSelectEstateModal(false);
            handleSelectEstate(estate);
          }}
        />
      )}
      {selectedEstate && showTrusuiteSearchPaymentModal && (
        <CreatePaymentModal
          paymentForm={
            <EstateTrusuiteSearchPaymentForm
              setShowModal={setShowTrusuiteSearchPaymentModal}
              estate={selectedEstate}
            />
          }
          setShowModal={setShowTrusuiteSearchPaymentModal}
          showModal={showTrusuiteSearchPaymentModal}
        />
      )}
      <CreatePaymentModal
        paymentForm={
          <EstateTaskInstancePaymentForm
            estateTaskInstance={estateTaskInstanceCurrentlyEditing as EstateTaskInstance}
            setShowModal={setShowDelegatePaymentModal}
            replaceEstateTaskInstance={onAssignTask}
            addEstateTaskInstance={onAssignTask}
          />
        }
        setShowModal={setShowDelegatePaymentModal}
        showModal={showDelegatePaymentModal}
      />
    </Box>
  );
};
